import FuzzySearch from 'fuzzy-search';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { UserRequest } from '../../../api/models/UserRequest';
import { apiConfigs } from '../../../api/APIConfigs';
import { useUserRoles } from '../../../util/use-user-role';
import { useAsyncRequest } from '../../../util/use-async-request';
import { useRequest } from '../../../util/use-request';
import { UserRequestsScreen } from '../components/UserRequestsScreen';

export type UserRequestsProps = {
  // Inputs
  /** list of user requests */
  requests: UserRequest[];
  /** paginated requests */
  paginatedRequests: UserRequest[];
  /** current page number */
  currentPage: number;
  /** total number of pages */
  totalPages: number;
  /** items per page */
  itemsPerPage: number;
  /** whether or not list of requests is fetching */
  isLoadingRequests: boolean;
  /** whether a processUserRequest action is in progress */
  isProcessingRequest: boolean;
  /** search term for filtering */
  searchTerm: string;
  /** status filter: 'all', 'pending', 'accepted', 'denied' */
  statusFilter: string;
  /** request type filter: 'all', 'account_deletion', 'role_change' */
  requestTypeFilter: string;
  /** current user role */
  userRole: string | undefined;
  /** whether user is System Admin */
  isSystemAdmin: boolean;
  /** whether delete confirmation modal is open */
  isDeleteModalOpen: boolean;
  /** request ID for delete modal */
  deleteRequestId: string | null;
  /** whether deny modal is open */
  isDenyModalOpen: boolean;
  /** request ID for deny modal */
  denyRequestId: string | null;

  // Outputs
  /** called when the search field receives a [change] event */
  userUpdatedSearchTerm: (term: string) => void;
  /** called when status filter changes */
  userUpdatedStatusFilter: (status: string) => void;
  /** called when request type filter changes */
  userUpdatedRequestTypeFilter: (type: string) => void;
  /** called when user changes page */
  userChangedPage: (page: number) => void;
  /** called when user accepts a request (no confirmation needed) */
  userAcceptedRequest: (requestId: string) => void;
  /** called when user clicks delete button */
  userClickedDelete: (requestId: string) => void;
  /** called when user confirms delete */
  userConfirmedDelete: (requestId: string) => void;
  /** called when user clicks deny button */
  userClickedDeny: (requestId: string) => void;
  /** called when user confirms deny with reason */
  userConfirmedDeny: (requestId: string, reason: string) => void;
  /** called when user closes delete modal */
  userClosedDeleteModal: () => void;
  /** called when user closes deny modal */
  userClosedDenyModal: () => void;
};

export const UserRequestsContainer = () => {
  const { roles } = useUserRoles();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [requestTypeFilter, setRequestTypeFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  const userRole = roles?.[0];
  const isSystemAdmin = userRole === 'System Admin';

  // Use state to manage requests so we can update them when actions are taken
  const [requests, setRequests] = useState<UserRequest[]>([]);
  const [isProcessingRequest, setIsProcessingRequest] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteRequestId, setDeleteRequestId] = useState<string | null>(null);
  const [isDenyModalOpen, setIsDenyModalOpen] = useState(false);
  const [denyRequestId, setDenyRequestId] = useState<string | null>(null);

  const request = useRequest();

  const { loading: isLoadingRequests } = useAsyncRequest<UserRequest[]>(
    useCallback(() => request<UserRequest[]>(apiConfigs.getUserRequestsPending()), [request]),
    useCallback(result => {
      const apiRequests: UserRequest[] = (result || []).map((item: any) => ({
        id: item.id,
        externalId: item.externalId || '',
        userEmail: item.userEmail || '',
        userRole: item.currentRole || '',
        churchId: item.churchId || '',
        churchName: item.churchKey || '',
        requestType: item.type === 'delete' ? 'account_deletion' : 'role_change',
        requestedRole: item.requestedRole || undefined,
        status: (item.status || 'pending') as any,
        createdAt: item.created ? new Date(item.created) : new Date(),
        updatedAt: item.updated ? new Date(item.updated) : undefined,
        actionTakenBy: undefined,
        actionTakenAt: undefined,
      }));

      setRequests(apiRequests);
    }, [])
  );

  const filteredByChurch = useMemo(() => {
    return requests;
  }, [requests]);

  // Apply status filter
  const statusFilteredRequests = useMemo(() => {
    if (statusFilter === 'all') return filteredByChurch;
    return filteredByChurch.filter(r => r.status === statusFilter);
  }, [filteredByChurch, statusFilter]);

  // Apply request type filter
  const typeFilteredRequests = useMemo(() => {
    if (requestTypeFilter === 'all') return statusFilteredRequests;
    return statusFilteredRequests.filter(r => r.requestType === requestTypeFilter);
  }, [statusFilteredRequests, requestTypeFilter]);

  // Search functionality - currently search by email / externalId / roles
  const requestSearcher = useMemo(
    () =>
      new FuzzySearch(typeFilteredRequests, ['userEmail', 'externalId', 'userRole', 'requestedRole'], {
        caseSensitive: false,
      }),
    [typeFilteredRequests]
  );

  const searchedRequests = useMemo(() => {
    if (!searchTerm.trim()) return typeFilteredRequests;
    return requestSearcher.search(searchTerm) as UserRequest[];
  }, [searchTerm, requestSearcher, typeFilteredRequests]);

  const totalPages = Math.ceil(searchedRequests.length / itemsPerPage);
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return searchedRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [searchedRequests, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, requestTypeFilter]);

  const handleAcceptRequest = async (requestId: string) => {
    const target = requests.find(r => r.id === requestId);
    if (!target) return;

    const isAccountDeletion = target.requestType === 'account_deletion';

    try {
      setIsProcessingRequest(true);
      await request(
        apiConfigs.processUserRequest(requestId, 'approved', isAccountDeletion ? 'ok and eligible' : 'ok and eligible')
      );

      setRequests(prevRequests =>
        prevRequests.map(req => {
          if (req.id === requestId) {
            const newStatus: UserRequest['status'] = isAccountDeletion ? 'deleted' : 'accepted';
            return {
              ...req,
              status: newStatus,
              updatedAt: new Date(),
              actionTakenBy: 'admin@example.com', // TODO: Get from current user
              actionTakenAt: new Date(),
            };
          }
          return req;
        })
      );
      toast.success(isAccountDeletion ? 'Account deletion approved.' : 'Request approved.');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to process request.');
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleConfirmDelete = async (requestId: string) => {
    try {
      setIsProcessingRequest(true);
      await handleAcceptRequest(requestId);
      setIsDeleteModalOpen(false);
      setDeleteRequestId(null);
    } finally {
      setIsProcessingRequest(false);
    }
  };

  const handleConfirmDeny = async (requestId: string, reason: string) => {
    try {
      setIsProcessingRequest(true);
      await request(apiConfigs.processUserRequest(requestId, 'rejected', reason || 'not eligible'));

      setRequests(prevRequests =>
        prevRequests.map(req =>
          req.id === requestId
            ? {
                ...req,
                status: 'denied',
                updatedAt: new Date(),
                actionTakenBy: 'admin@example.com', // TODO: Get from current user
                actionTakenAt: new Date(),
              }
            : req
        )
      );
      toast.success('Request denied.');
      setIsDenyModalOpen(false);
      setDenyRequestId(null);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to deny request.');
    } finally {
      setIsProcessingRequest(false);
    }
  };

  return (
    <UserRequestsScreen
      requests={searchedRequests}
      paginatedRequests={paginatedRequests}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      isLoadingRequests={isLoadingRequests}
      isProcessingRequest={isProcessingRequest}
      searchTerm={searchTerm}
      statusFilter={statusFilter}
      requestTypeFilter={requestTypeFilter}
      userRole={userRole}
      isSystemAdmin={isSystemAdmin}
      isDeleteModalOpen={isDeleteModalOpen}
      deleteRequestId={deleteRequestId}
      isDenyModalOpen={isDenyModalOpen}
      denyRequestId={denyRequestId}
      userUpdatedSearchTerm={setSearchTerm}
      userUpdatedStatusFilter={setStatusFilter}
      userUpdatedRequestTypeFilter={setRequestTypeFilter}
      userChangedPage={setCurrentPage}
      userAcceptedRequest={handleAcceptRequest}
      userClickedDelete={requestId => {
        setDeleteRequestId(requestId);
        setIsDeleteModalOpen(true);
      }}
      userConfirmedDelete={handleConfirmDelete}
      userClickedDeny={requestId => {
        setDenyRequestId(requestId);
        setIsDenyModalOpen(true);
      }}
      userConfirmedDeny={handleConfirmDeny}
      userClosedDeleteModal={() => {
        setIsDeleteModalOpen(false);
        setDeleteRequestId(null);
      }}
      userClosedDenyModal={() => {
        setIsDenyModalOpen(false);
        setDenyRequestId(null);
      }}
    />
  );
};
